import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./Terminal.css";

const Terminal = ({ isOpen, onClose }) => {
  const [input, setInput] = useState("");
  const [history, setHistory] = useState([]);
  const [currentPath, setCurrentPath] = useState("phoenix-cybersecurity");
  const [currentTime, setCurrentTime] = useState("");
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      // Add welcome message when terminal opens
      const welcomeMessage = {
        type: "system",
        content: "PHOENIX CYBERSECURITY TERMINAL v4.0 - ACCESS GRANTED",
        timestamp: new Date().toLocaleTimeString(),
      };
      setHistory([welcomeMessage]);
    }
  }, [isOpen]);

  // Dynamic clock update
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const timeString = now.toLocaleTimeString();
      setCurrentTime(timeString);
    };

    // Update immediately
    updateTime();

    // Set up interval to update every second
    const timer = setInterval(updateTime, 1000);

    return () => clearInterval(timer);
  }, []);

  const commands = {
    "home base": () => navigate("/"),
    home: () => navigate("/"),
    about: () => navigate("/about"),
    "rules and regulations": () => navigate("/mission-briefing"),
    "mission briefing": () => navigate("/mission-briefing"),
    briefing: () => navigate("/mission-briefing"),
    register: () => navigate("/register"),
    registration: () => navigate("/register"),
    help: () => {
      const helpText = `
Available Commands:
- home base / home          : Navigate to home page
- about                     : Navigate to about page
- rules and regulations / mission briefing / briefing : Navigate to rules and regulations
- register / registration   : Navigate to registration
- clear                     : Clear terminal history
- help                      : Show this help message
- exit / close              : Close terminal
      `;
      return helpText.trim();
    },
    clear: () => {
      setHistory([]);
      return null;
    },
    exit: () => onClose(),
    close: () => onClose(),
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const command = input.toLowerCase().trim();
    const timestamp = new Date().toLocaleTimeString();

    // Add user input to history
    const userEntry = {
      type: "user",
      content: `${currentPath}@phoenix:~$ ${input}`,
      timestamp,
    };

    let response = null;

    if (commands[command]) {
      const result = commands[command]();
      if (typeof result === "string") {
        response = {
          type: "system",
          content: result,
          timestamp,
        };
      }
    } else {
      response = {
        type: "error",
        content: `Command not found: ${command}. Type 'help' for available commands.`,
        timestamp,
      };
    }

    setHistory((prev) => {
      const newHistory = [...prev, userEntry];
      if (response) {
        newHistory.push(response);
      }
      return newHistory;
    });

    setInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="terminal-overlay" onClick={onClose}>
      <div className="terminal-container" onClick={(e) => e.stopPropagation()}>
        <div className="terminal-header">
          <div className="terminal-title">
            <span className="terminal-icon">⚡</span>
            PHOENIX CYBERSECURITY TERMINAL
            <span className="terminal-clock">{currentTime}</span>
          </div>
          <div className="terminal-controls">
            <button className="control-btn minimize" onClick={onClose}>
              −
            </button>
            <button className="control-btn maximize">□</button>
            <button className="control-btn close" onClick={onClose}>
              ×
            </button>
          </div>
        </div>

        <div className="terminal-body">
          <div className="terminal-content">
            {history.map((entry, index) => (
              <div key={index} className={`terminal-line ${entry.type}`}>
                <span className="terminal-timestamp">[{entry.timestamp}]</span>
                <span className="terminal-text">{entry.content}</span>
              </div>
            ))}
          </div>

          <form className="terminal-input-form" onSubmit={handleSubmit}>
            <span className="terminal-prompt">{currentPath}@phoenix:~$</span>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="terminal-input"
              placeholder="Type a command..."
              autoComplete="off"
            />
          </form>
        </div>
      </div>
    </div>
  );
};

export default Terminal;
